import { memo, type ReactElement, useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { hasPromptText } from '../../domain/promptText'
import type { AgentWidgetMode } from '../../domain/widgetMode'
import { useAgentEscapeKey } from '../../hooks/useAgentEscapeKey'
import { useCompactIdleTimeout } from '../../hooks/useCompactIdleTimeout'
import {
  AgentCanSendContext,
  AgentRunningTurnContext,
  AgentTurnStoppingContext,
  useAgentChatActions,
} from '../../state/agentWidgetContext'
import { AgentChatPane } from '../chat/AgentChatPane'
import { AgentPromptInput } from '../prompt/AgentPromptInput'
import type { AgentApprovalPolicy } from '../prompt/approvalMode'
import { AGENT_FRAME_SELECTOR } from './agentFrameMarker'
import { AgentHeader } from './AgentHeader'
import { AgentPanelResizeHandles } from './AgentPanelResizeHandles'
import { type AgentPanelSize, frameStyleOf } from './agentWidgetLayout'

interface AgentFrameProperties {
  mode: Exclude<AgentWidgetMode, 'collapsed'>
  // 利用者が変えたパネルの大きさ。変えていなければ null で、既定の大きさで出す
  panelSize: AgentPanelSize | null
  // 書いている指示（AgentWidget が持つ）
  prompt: string
  // まだ指示を送っていない（始め直すものがない）ときは false
  canStartNewChat: boolean
  // 別の会話に切り替えた回数（selectOpenedChatCount）。会話の表示の key にする
  openedChatCount: number
  // 承認モード（AgentWidget が持ち、ブラウザに覚える）
  approvalPolicy: AgentApprovalPolicy
  onPromptChange: (prompt: string) => void
  onApprovalPolicyChange: (approvalPolicy: AgentApprovalPolicy) => void
  onExpand: () => void
  // × と Esc で入口に戻す
  onClose: () => void
  // 入力欄のまま操作がなかった
  onIdleTimeout: () => void
  onPanelResize: (size: AgentPanelSize) => void
}

/**
 * モードを変えても入力欄の編集状態とフォーカスを保つため、部品の並びをそろえる。
 * 枠は会話の内容を購読せず、回答の増分では再描画しない。
 */
const AgentFrameView = ({
  mode,
  panelSize,
  prompt,
  canStartNewChat,
  openedChatCount,
  approvalPolicy,
  onPromptChange,
  onApprovalPolicyChange,
  onExpand,
  onClose,
  onIdleTimeout,
  onPanelResize,
}: AgentFrameProperties): ReactElement => {
  const { t } = useTranslation()
  const { sendPrompt, stopTurn, startNewChat } = useAgentChatActions()
  const isRunning = useContext(AgentRunningTurnContext) !== null
  const isStopping = useContext(AgentTurnStoppingContext)
  const canSend = useContext(AgentCanSendContext)
  // 閉じるつもりの Esc で実行を中断しない。停止は利用者が停止ボタンで指示する。
  const handleEscape = useAgentEscapeKey(onClose)
  // 入力欄に送れる中身があるときは、操作がなくても入口に戻さない。書いた指示を失わないためである
  useCompactIdleTimeout(mode === 'compact' && !hasPromptText(prompt), onIdleTimeout)
  // 入力欄からパネルに移ると、押した「広げる」のボタンが消えて、フォーカスが枠の外（body）に落ちる。
  // フォーカスが外れたままだと、枠の Esc は届かず、document の Esc もポインターが枠の上にあるときしか効かないので、
  // キーボードだけで操作していてもパネルを Esc で閉じられるよう、枠にフォーカスを戻す。
  // 入口から入力欄を開いたときは戻さない。ウィジェットの中にフォーカスがあると、操作がないときに入口へ戻す判定が止まるためである
  useEffect(() => {
    if (mode !== 'expanded') {
      return
    }
    const { activeElement } = document
    if (activeElement === null || activeElement === document.body) {
      document.querySelector<HTMLElement>(AGENT_FRAME_SELECTOR)?.focus()
    }
  }, [mode])

  // 指示を送ったら、回答を出せるパネルに広げる（仕様の「段階を移る条件」）。入力欄は memo なので、同じ関数を渡し続ける
  const send = useCallback(
    (text: string): boolean => {
      const isSent = sendPrompt(text)
      if (isSent) {
        onExpand()
      }
      return isSent
    },
    [onExpand, sendPrompt],
  )
  // 新しいチャットは空の入力欄から始める（仕様の「書きかけの指示」）
  const startNewChatWithEmptyPrompt = useCallback((): void => {
    startNewChat()
    onPromptChange('')
  }, [onPromptChange, startNewChat])

  return (
    <section
      data-agent-frame=""
      // 上の処理でフォーカスを受けるため。Tab の順には入れない
      tabIndex={-1}
      style={frameStyleOf(mode, panelSize)}
      aria-label={t(`エージェント`)}
      className="flex flex-col gap-2 rounded-lg border border-solid border-gray-200 bg-white p-2 shadow-lg outline-none"
      onKeyDown={handleEscape}
    >
      {mode === 'expanded' && <AgentPanelResizeHandles onResize={onPanelResize} />}
      {mode === 'compact' ? (
        <AgentHeader
          mode="compact"
          canStartNewChat={canStartNewChat}
          onNewChat={startNewChatWithEmptyPrompt}
          onExpand={onExpand}
          onClose={onClose}
        />
      ) : (
        <AgentHeader
          mode="expanded"
          canStartNewChat={canStartNewChat}
          onNewChat={startNewChatWithEmptyPrompt}
          onClose={onClose}
        />
      )}
      {mode === 'expanded' && <AgentChatPane key={openedChatCount} />}
      <AgentPromptInput
        prompt={prompt}
        onPromptChange={onPromptChange}
        isRunning={isRunning}
        isStopping={isStopping}
        canSend={canSend}
        onSend={send}
        onStop={stopTurn}
        approvalPolicy={mode === 'expanded' ? approvalPolicy : null}
        onApprovalPolicyChange={onApprovalPolicyChange}
      />
    </section>
  )
}

// 回答が伸びてウィジェットが描き直されても、受け取る値が同じなら描き直さない
export const AgentFrame = memo(AgentFrameView)
